import React, { useState } from "react"
import { AlertMessage, AssetMap, Round, TeamRound } from "../types"
import Scorecard from "./Scorecard"
import ConfirmLowScores from "./ConfirmLowScores"

interface ReviewScoresProps {
  round: Round
  pairing: TeamRound[]
  assets: AssetMap
  postRoundComplete()
  enterScores()
  showAlert: (message: AlertMessage) => void
}

export default function ReviewScores({
  round,
  pairing,
  assets,
  postRoundComplete,
  enterScores,
  showAlert
}: ReviewScoresProps) {
  const { holes } = round
  const [completingRound, setCompletingRound] = useState(false)
  const teamRound = pairing[0]
  const { players } = teamRound

  const missingPartnerScores =
    teamRound.players[1] &&
    !teamRound.players[1].canceled &&
    (!teamRound.players[1].scores || teamRound.players[1].scores.size < round.numberOfHoles)

  const confirmationRequired = players.some((player) => {
    return player.netScoreRelativeToPar <= -round.numberOfHoles
  })
  const [roundCompletable, setRoundCompletable] = useState<boolean>(!confirmationRequired)

  const midnightDayOfRound = new Date(new Date(round.startsAt).setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000)

  const completeRound = async () => {
    setCompletingRound(true)
    await postRoundComplete()
    setCompletingRound(false)
  }

  const checkPartnerScoresAndCompleteRound = async () => {
    if (!roundCompletable) {
      return
    }

    if (missingPartnerScores) {
      showAlert({
        title: "Missing Partner Scores",
        message:
          "You are missing scores for your partner. Once you complete your round, you will not be able to add the missing scores for your partner. Are you sure you want to complete your round anyway?",
        buttons: [
          { text: "Yes, complete round anyway", style: "primary", onPress: completeRound },
          { text: "No, enter partner scores" }
        ]
      })
    } else {
      await completeRound()
    }
  }

  return (
    <div className="screen-body">
      <div className="screen-heading">
        <h1 className="screen-title">{missingPartnerScores ? "Waiting For Partner Scores" : "Review Scorecard"}</h1>
        <p>
          {missingPartnerScores
            ? "You have entered all your scores but your partner has not. Please enter their scores or wait for them to do so before completing your round. "
            : "Make sure your scores are correct before completing your round. "}
          Once you complete your round, you will not be able to change the scores for you or your partner. Results will
          be sent at noon{new Date() < midnightDayOfRound ? " tomorrow" : ""}.
        </p>
      </div>
      <div className="screen-body">
        <div className="card-v2">
          <div className="container">
            <div className="post-hole-results">
              <section>
                {pairing.map((tr, i) => {
                  return <Scorecard key={i} holes={holes} teamRound={tr} numberOfHoles={round.numberOfHoles} holeIndex={0} />
                })}
              </section>
            </div>
          </div>
        </div>

        {confirmationRequired && (
          <ConfirmLowScores teamRound={teamRound} allowRoundComplete={(value: boolean) => setRoundCompletable(value)} />
        )}

        <div style={{ marginTop: "2rem", width: "100%", textAlign: "center" }}>
          <a
            onClick={checkPartnerScoresAndCompleteRound}
            className={`action-button ${roundCompletable ? "" : "disabled"}`}
            style={{ width: "100%", maxWidth: "20rem" }}
          >
            {completingRound ? (
              <img src={assets.buttonSpinner} style={{ paddingTop: "4px" }} alt="Completing Round..." />
            ) : (
              "Complete Round"
            )}
          </a>
        </div>
        <div style={{ marginTop: "2rem", textAlign: "center" }}>
          <a onClick={enterScores} style={{ color: "black", textDecoration: "underline" }}>
            Edit Scores
          </a>
        </div>
      </div>
    </div>
  )
}
